/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { OpenEndedAnswerType, ExternalQuestion } from '@sm/question-widgets/respondent-survey';
import { AppDispatch, RootState } from '~app/store';
import {
  QuestionAnswers,
  SubmitMutation,
  CurrentSessionSurvey,
  CurrentSessionCollector,
  CurrentSessionCurrentPage,
  RespondentSession,
} from './types';
import { SurveyFormat } from '~app/components/Survey/SurveyFormat/constants';
import { validate, deferToAfterFocusChanged } from './validation';
import { transformToSurveyErrors } from './errors/transformToSurveyErrors';
import { setErrorsById, removeErrorsById } from './errors/errorsSlice';
import { mapSurveyAnswersToResponse } from './helper/mapSurveyAnswers';
import { DEFAULT_SUBMIT_RESULT } from './constants';

// ========== INITIAL STATE

export type SurveyState = {
  /** The survey object */
  survey?: CurrentSessionSurvey;
  /** The current page object */
  currentPage?: CurrentSessionCurrentPage;
  /** The collector object */
  collector?: CurrentSessionCollector;
  /** The client token */
  clientToken?: string;
  /** The answers for the current page */
  answers: QuestionAnswers;
};

export const initialState: SurveyState = {
  survey: undefined,
  currentPage: undefined,
  collector: undefined,
  clientToken: undefined,
  answers: {},
};

// ========== SLICE

const sliceName = 'survey';

export const surveySlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    initializeSurvey: (state, action: PayloadAction<RespondentSession>) => {
      state.survey = action.payload.survey;
      state.currentPage = action.payload.currentPage;
      state.collector = action.payload.collector;

      // initialize answers
      state.answers =
        action.payload.currentPage?.questionsQM?.items?.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.id]: {
              questionId: curr.id,
              values: [],
              touched: false,
              isDirty: false,
            },
          }),
          {}
        ) ?? {};
    },
    setAnswers: (state, action: PayloadAction<QuestionAnswers>) => {
      state.answers = action.payload;
    },
    updateAnswers: (state, action: PayloadAction<QuestionAnswers>) => {
      state.answers = { ...state.answers, ...action.payload };
    },
    setClientToken: (state, action: PayloadAction<string>) => {
      state.clientToken = action.payload;
    },
    reset: () => initialState,
  },
});

// ========== ACTIONS

export const { initializeSurvey, setAnswers, updateAnswers, setClientToken, reset } = surveySlice.actions;

// ========== THUNKS

/**
 * Update an answer - the specific answer is inferred by the `questionId` inside the answer object.
 * Error validation is also run for the new answer and the errors state is updated accordingly.
 *  */
export const updateAnswer = createAsyncThunk(`${sliceName}/updateAnswer`, (answer: OpenEndedAnswerType, thunkAPI) => {
  const { questionId } = answer;
  const { surveyState } = thunkAPI.getState() as RootState;
  const dispatch = thunkAPI.dispatch as AppDispatch;

  if (!answer.touched) {
    return; // no need for updates if answer was not touched
  }

  const { isValid, errors } = validate(questionId);
  deferToAfterFocusChanged(() => {
    // defer to make done with error-change click work
    if (!isValid) {
      const questionErrors = transformToSurveyErrors(errors);
      dispatch(setErrorsById({ id: questionId, newErrors: questionErrors }));
    } else {
      dispatch(removeErrorsById(questionId));
    }
  });

  dispatch(
    updateAnswers({
      [questionId]: { ...surveyState.answers[questionId], ...answer },
    })
  );
});

/**
 * Submit the survey response.
 * Currently requires the mutation function to be passed as a parameter as it is generated by hook.
 * Note: dateStarted and dateCompleted also need to be passed in as part of a solution to
 * fix failing integration tests involving mocked timers and promises.
 *  */
export const submitSurvey = createAsyncThunk(
  `${sliceName}/submit`,
  async (
    {
      mappedQuestions,
      mutation,
      dateStarted,
      dateCompleted,
    }: { mappedQuestions: ExternalQuestion[]; mutation: SubmitMutation; dateStarted: string; dateCompleted: string },
    { getState }
  ) => {
    const { surveyState } = getState() as RootState;
    if (surveyState.survey) {
      try {
        // Only use the answered questions in payload (SPAGE-7410)
        const responses = mapSurveyAnswersToResponse({
          answers: surveyState.answers,
          currentPageId: surveyState.currentPage?.id ?? '',
          mappedQuestions,
        });

        const { data: { submitSurveyResponse } = {} } = await mutation({
          variables: {
            surveyId: surveyState.survey.id ?? '',
            input: {
              collectorId: surveyState.collector?.id ?? '',
              isAnonymous: surveyState.collector?.isAnonymous ?? false,
              surveyVersion: surveyState.survey.version ?? 1,
              dateStarted,
              dateCompleted,
              respondentId: '',
              responses,
            },
          },
        });

        return submitSurveyResponse;
      } catch (error: unknown) {
        // To be completed in server error validation story
        console.log(error);
      }
    }
    return DEFAULT_SUBMIT_RESULT;
  }
);

export const nextPage = createAsyncThunk(`${sliceName}/nextPage`, () => {
  console.log('Use this method to get the next page.  Pending decision about pagination');
});

export const previousPage = createAsyncThunk(`${sliceName}/previousPage`, () => {
  console.log('Use this method to get the previous page.  Pending decision about pagination');
});

// ========== SELECTORS

/** The survey state object */
const selectSurveyState = (state: RootState): SurveyState => state.surveyState;

/** The survey object */
export const selectSurvey = createSelector(selectSurveyState, surveyState => surveyState.survey);

/** The current page object */
export const selectCurrentPage = createSelector(selectSurveyState, surveyState => surveyState.currentPage);

/** The collector object */
export const selectCollector = createSelector(selectSurveyState, surveyState => surveyState.collector);

/** The client token */
export const selectClientToken = createSelector(selectSurveyState, surveyState => surveyState.clientToken);

/** The survey format (eg. classic or OQAAT) */
export const selectSurveyFormat = createSelector(
  selectSurvey,
  survey => survey?.format ?? SurveyFormat.ONE_QUESTION_AT_A_TIME
);

/** Array containing all questions on the current page */
export const selectQuestions = createSelector(selectCurrentPage, currentPage => currentPage?.questionsQM?.items ?? []);

/** Object containing all answers for the current page */
export const selectAnswers = createSelector(selectSurveyState, surveyState => surveyState.answers);

/** The answer object for the question specified by Id */
export const selectAnswerById = createSelector(
  [selectAnswers, (_, questionId: string) => questionId],
  (answers, questionId) => answers[questionId]
);

/**
 * The total number of questions that have a value (i.e. answered), on the current page
 * note: currently only works with single-value questions
 * TODO: refactor to include answered questions on other pages once we get that data from the backend
 *  */
export const selectTotalAnswers = createSelector(selectAnswers, answers =>
  !answers ? 0 : Object.values(answers).reduce((acc: number, answer) => (answer.values?.[0]?.value ? acc + 1 : acc), 0)
);

// ========== EXPORT

export default surveySlice.reducer;
